import React, { useEffect, useMemo, useState } from 'react';
import { Layout } from '../components/Layout';
import { Search, Filter, Users, MapPin, Mail, Phone, Building, User, CreditCard as Edit, Trash2, Plus, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

type Contato = {
  id: string;
  nome: string;
  cargo?: string | null;
  setor?: string | null;
  localizacao?: string | null;
  ramal?: string | null;
  telefone?: string | null;
  email?: string | null;
  tipo?: string;
  ativo: boolean;
  foto?: string | null;
};

const Diretorio: React.FC = () => {
  const { user } = useAuth();
  const [contatos, setContatos] = useState<Contato[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState<Contato | null>(null);

  const [q, setQ] = useState('');
  const [setor, setSetor] = useState<string>('');
  const [cidade, setCidade] = useState<string>('');
  const [cargo, setCargo] = useState<string>('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const [formData, setFormData] = useState({
    nome: '',
    cargo: '',
    setor: '',
    email: '',
    telefone: '',
    ramal: '',
    localizacao: '',
    tipo: 'equipe'
  });

  const isAdmin = user?.role === 'admin' || user?.setor === 'TI' || user?.setor === 'RH';

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('contatos')
        .select('*, usuarios!contatos_usuario_id_fkey(picture)')
        .eq('ativo', true);

      if (error) throw error;

      const mapped = (data || []).map((c: any) => ({
        ...c,
        foto: c.usuarios?.picture || null,
        usuarios: undefined,
      }));

      const sorted = mapped.sort((a: Contato, b: Contato) => {
        if (a.tipo !== b.tipo) {
          if (a.tipo === 'equipe') return -1;
          if (b.tipo === 'equipe') return 1;
          if (a.tipo === 'representante') return 1;
          if (b.tipo === 'representante') return -1;
        }
        return a.nome.localeCompare(b.nome, 'pt-BR');
      });

      setContatos(sorted);
    } catch (error) {
      console.error('Error loading contacts:', error);
      toast.error('Erro ao carregar contatos');
    } finally {
      setLoading(false);
    }
  };

  const setores = useMemo(() => {
    const s = new Set<string>();
    contatos.forEach(c => {
      const v = (c.setor || '').trim();
      if (v) s.add(v);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [contatos]);

  const cidades = useMemo(() => {
    const s = new Set<string>();
    contatos.forEach(c => {
      const v = (c.localizacao || '').trim();
      if (v) s.add(v);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [contatos]);

  const cargos = useMemo(() => {
    const s = new Set<string>();
    contatos.forEach(c => {
      const v = (c.cargo || '').trim();
      if (v) s.add(v);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [contatos]);

  const filtrados = useMemo(() => {
    const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return contatos.filter(c => {
      const matchTexto = terms.length === 0 || terms.every(term =>
        [c.nome, c.cargo, c.setor, c.localizacao, c.telefone, c.email]
          .filter(Boolean)
          .some(v => String(v).toLowerCase().includes(term))
      );

      const matchSetor = !setor || (c.setor || '') === setor;
      const matchCidade = !cidade || (c.localizacao || '') === cidade;
      const matchCargo = !cargo || (c.cargo || '') === cargo;

      return matchTexto && matchSetor && matchCidade && matchCargo;
    });
  }, [contatos, q, setor, cidade, cargo]);

  const highlightText = (text: string, searchTerms: string[]) => {
    if (!text || searchTerms.length === 0) return text;

    let highlighted = text;
    searchTerms.forEach(term => {
      if (term.length > 1) {
        const regex = new RegExp(`(${term})`, 'gi');
        highlighted = highlighted.replace(regex, '<mark class="bg-primary/20 px-0.5 rounded">$1</mark>');
      }
    });

    return highlighted;
  };

  const searchTerms = q.trim().toLowerCase().split(/\s+/).filter(Boolean);

  const handleEdit = (contact: Contato) => {
    setEditingContact(contact);
    setFormData({
      nome: contact.nome,
      cargo: contact.cargo || '',
      setor: contact.setor || '',
      email: contact.email || '',
      telefone: contact.telefone || '',
      ramal: contact.ramal || '',
      localizacao: contact.localizacao || '',
      tipo: contact.tipo || 'equipe'
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja remover este contato?')) return;

    try {
      const { error } = await supabase
        .from('contatos')
        .update({ ativo: false })
        .eq('id', id);

      if (error) throw error;
      toast.success('Contato removido com sucesso');
      loadContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Erro ao remover contato');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nome.trim()) {
      toast.error('Nome e obrigatorio');
      return;
    }

    try {
      if (editingContact) {
        const { error } = await supabase
          .from('contatos')
          .update({
            ...formData,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingContact.id);

        if (error) throw error;
        toast.success('Contato atualizado com sucesso');
      } else {
        const { error } = await supabase
          .from('contatos')
          .insert([formData]);

        if (error) throw error;
        toast.success('Contato adicionado com sucesso');
      }

      setShowModal(false);
      setEditingContact(null);
      resetForm();
      loadContacts();
    } catch (error: any) {
      console.error('Error saving contact:', error);
      toast.error('Erro ao salvar contato: ' + (error.message || 'Erro desconhecido'));
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      cargo: '',
      setor: '',
      email: '',
      telefone: '',
      ramal: '',
      localizacao: '',
      tipo: 'equipe'
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingContact(null);
    resetForm();
  };

  const ContactCard = ({ c }: { c: Contato }) => (
    <div className="p-4 border border-outline-variant dark:border-gray-700 rounded-xl hover:border-primary/30 hover:shadow-md transition-all relative group bg-white dark:bg-gray-800">
      {isAdmin && (
        <div className="absolute top-3 right-3 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => handleEdit(c)}
            className="p-1.5 bg-primary/10 text-primary rounded-lg hover:bg-primary/20"
            title="Editar"
          >
            <Edit className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => handleDelete(c.id)}
            className="p-1.5 bg-error/10 text-error rounded-lg hover:bg-error/20"
            title="Remover"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      <div className="flex items-start space-x-3">
        {c.foto ? (
          <img
            src={c.foto}
            alt={c.nome}
            className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
            <User className="w-6 h-6 text-white" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div
            className="text-base font-bold text-on-surface truncate"
            dangerouslySetInnerHTML={{
              __html: highlightText(c.nome, searchTerms)
            }}
          />
          {c.cargo && (
            <div
              className="text-sm text-secondary truncate"
              dangerouslySetInnerHTML={{
                __html: highlightText(c.cargo, searchTerms)
              }}
            />
          )}
          {c.localizacao && (
            <div className="flex items-center space-x-1 mt-1">
              <MapPin className="w-3 h-3 text-secondary" />
              <span
                className="text-xs text-secondary"
                dangerouslySetInnerHTML={{
                  __html: highlightText(c.localizacao, searchTerms)
                }}
              />
            </div>
          )}
          <div className="mt-2 space-y-1">
            {c.telefone && (
              <div className="flex items-center space-x-2">
                <Phone className="w-3 h-3 text-secondary" />
                <span
                  className="text-sm text-on-surface"
                  dangerouslySetInnerHTML={{
                    __html: highlightText(c.telefone, searchTerms)
                  }}
                />
              </div>
            )}
            {c.email && (
              <div className="flex items-center space-x-2">
                <Mail className="w-3 h-3 text-secondary" />
                <span
                  className="text-sm text-on-surface truncate"
                  dangerouslySetInnerHTML={{
                    __html: highlightText(c.email, searchTerms)
                  }}
                />
              </div>
            )}
            {c.ramal && (
              <div className="flex items-center space-x-2">
                <Phone className="w-3 h-3 text-primary" />
                <span className="text-sm text-secondary">Ramal {c.ramal}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-headline-lg text-on-surface mb-1">Diretorio Corporativo</h1>
            <p className="text-body-md text-secondary">Encontre rapidamente qualquer colaborador ou contato da empresa.</p>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-lg hover:bg-primary-dark transition-colors font-bold text-sm shadow-sm active:scale-95 duration-150"
              >
                <Plus className="w-4 h-4" />
                <span>Novo Contato</span>
              </button>
            )}
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-colors font-medium text-sm ${
                showAdvancedFilters
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-outline-variant text-secondary hover:bg-surface-container-low'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span>Filtros</span>
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-secondary w-5 h-5" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Buscar por nome, cargo, setor, cidade, telefone ou e-mail..."
              className="w-full pl-12 pr-4 py-3 border border-outline-variant rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all bg-surface-container-lowest text-on-surface"
            />
          </div>

          {showAdvancedFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-5 mt-5 border-t border-outline-variant">
              <div>
                <label className="block text-sm font-medium text-on-surface mb-2">Setor</label>
                <select
                  value={setor}
                  onChange={e => setSetor(e.target.value)}
                  className="w-full border border-outline-variant rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                >
                  <option value="">Todos os setores</option>
                  {setores.map(s => (<option key={s} value={s}>{s}</option>))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface mb-2">Cidade</label>
                <select
                  value={cidade}
                  onChange={e => setCidade(e.target.value)}
                  className="w-full border border-outline-variant rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                >
                  <option value="">Todas as cidades</option>
                  {cidades.map(c => (<option key={c} value={c}>{c}</option>))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface mb-2">Cargo</label>
                <select
                  value={cargo}
                  onChange={e => setCargo(e.target.value)}
                  className="w-full border border-outline-variant rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                >
                  <option value="">Todos os cargos</option>
                  {cargos.map(c => (<option key={c} value={c}>{c}</option>))}
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => { setSetor(''); setCidade(''); setCargo(''); setQ(''); }}
                  className="w-full px-4 py-2.5 border border-outline-variant text-secondary rounded-lg hover:bg-surface-container-low transition-colors font-medium"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>
          )}

          {(setor || cidade || cargo || q) && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-outline-variant">
              {q && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary/10 text-primary font-medium">
                  Busca: "{q}"
                  <button onClick={() => setQ('')} className="ml-2 hover:text-primary-dark font-bold">x</button>
                </span>
              )}
              {setor && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary/10 text-primary font-medium">
                  Setor: {setor}
                  <button onClick={() => setSetor('')} className="ml-2 hover:text-primary-dark font-bold">x</button>
                </span>
              )}
              {cidade && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary/10 text-primary font-medium">
                  Cidade: {cidade}
                  <button onClick={() => setCidade('')} className="ml-2 hover:text-primary-dark font-bold">x</button>
                </span>
              )}
              {cargo && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary/10 text-primary font-medium">
                  Cargo: {cargo}
                  <button onClick={() => setCargo('')} className="ml-2 hover:text-primary-dark font-bold">x</button>
                </span>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-outline-variant dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <div className="text-2xl font-bold text-on-surface">{contatos.length}</div>
                <div className="text-xs text-secondary font-medium uppercase tracking-wider">Total</div>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-outline-variant dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Building className="w-5 h-5 text-primary" />
              </div>
              <div>
                <div className="text-2xl font-bold text-on-surface">{setores.length}</div>
                <div className="text-xs text-secondary font-medium uppercase tracking-wider">Setores</div>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-outline-variant dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-primary" />
              </div>
              <div>
                <div className="text-2xl font-bold text-on-surface">{cidades.length}</div>
                <div className="text-xs text-secondary font-medium uppercase tracking-wider">Cidades</div>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-outline-variant dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Search className="w-5 h-5 text-primary" />
              </div>
              <div>
                <div className="text-2xl font-bold text-on-surface">{filtrados.length}</div>
                <div className="text-xs text-secondary font-medium uppercase tracking-wider">Resultados</div>
              </div>
            </div>
          </div>
        </div>

        {filtrados.length > 0 && (
          <div className="space-y-6">
            {setores.filter(s => filtrados.some(c => c.setor === s)).map(setorAtual => (
              <div key={setorAtual} className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
                <div className="flex items-center space-x-3 mb-5">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Building className="w-4 h-4 text-primary" />
                  </div>
                  <h2 className="text-lg font-bold text-on-surface">{setorAtual}</h2>
                  <span className="px-3 py-1 text-xs font-bold bg-primary/10 text-primary rounded-full">
                    {filtrados.filter(c => c.setor === setorAtual).length} pessoa(s)
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filtrados.filter(c => c.setor === setorAtual).map((c) => (
                    <ContactCard key={c.id} c={c} />
                  ))}
                </div>
              </div>
            ))}

            {filtrados.filter(c => !c.setor).length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
                <div className="flex items-center space-x-3 mb-5">
                  <div className="w-8 h-8 bg-surface-container rounded-lg flex items-center justify-center">
                    <Users className="w-4 h-4 text-secondary" />
                  </div>
                  <h2 className="text-lg font-bold text-on-surface">Outros Contatos</h2>
                  <span className="px-3 py-1 text-xs font-bold bg-surface-container text-secondary rounded-full">
                    {filtrados.filter(c => !c.setor).length} pessoa(s)
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filtrados.filter(c => !c.setor).map((c) => (
                    <ContactCard key={c.id} c={c} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && filtrados.length === 0 && contatos.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-12 text-center border border-outline-variant dark:border-gray-700">
            <Search className="w-16 h-16 text-secondary/40 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-on-surface mb-2">Nenhum resultado encontrado</h3>
            <p className="text-secondary">
              Tente ajustar os filtros ou usar termos de busca diferentes.
            </p>
          </div>
        )}

        {loading && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-12 text-center border border-outline-variant dark:border-gray-700">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-secondary">Carregando diretorio corporativo...</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="bg-primary px-6 py-5 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-white">
                    {editingContact ? 'Editar Contato' : 'Novo Contato'}
                  </h3>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">
                      Nome *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      className="w-full px-3 py-2.5 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">
                      Cargo
                    </label>
                    <input
                      type="text"
                      value={formData.cargo}
                      onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                      className="w-full px-3 py-2.5 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">
                      Setor
                    </label>
                    <input
                      type="text"
                      value={formData.setor}
                      onChange={(e) => setFormData({ ...formData, setor: e.target.value })}
                      className="w-full px-3 py-2.5 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2.5 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">
                      Telefone
                    </label>
                    <input
                      type="text"
                      value={formData.telefone}
                      onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                      className="w-full px-3 py-2.5 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">
                      Ramal
                    </label>
                    <input
                      type="text"
                      value={formData.ramal}
                      onChange={(e) => setFormData({ ...formData, ramal: e.target.value })}
                      className="w-full px-3 py-2.5 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">
                      Localizacao
                    </label>
                    <input
                      type="text"
                      value={formData.localizacao}
                      onChange={(e) => setFormData({ ...formData, localizacao: e.target.value })}
                      className="w-full px-3 py-2.5 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      placeholder="ex: Apucarana - PR"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">
                      Tipo
                    </label>
                    <select
                      value={formData.tipo}
                      onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                      className="w-full px-3 py-2.5 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    >
                      <option value="equipe">Equipe</option>
                      <option value="representante">Representante</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 pt-5 border-t border-outline-variant">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-5 py-2.5 text-on-surface border border-outline-variant rounded-lg hover:bg-surface-container-low transition-colors font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-bold"
                  >
                    {editingContact ? 'Atualizar' : 'Adicionar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Diretorio;
export { Diretorio };
